import {
  ChainData,
  ChainType,
  CosmosChain,
  EvmChain,
} from "@0xsquid/squid-types";
import { useCallback, useMemo } from "react";
import { SwapDirection } from "../../core/types/swap";
import { filterChains, getSupportedChainIdsForDirection } from "../../services";
import { useSquid } from "../squid/useSquid";
import { useConfigStore } from "../store/useSquidStore";

// Memoized map to store chain data, calculated only once
let memoizedChainsByIdMap: Record<string, ChainData> | null = null;

// Optimize calculation to prevent unnecessary re-renders
// This ensures the map is created only once when chains are available
const createChainsByIdMap = (
  chains: ChainData[]
): Record<string, ChainData> | null => {
  if (memoizedChainsByIdMap) return memoizedChainsByIdMap;
  if (chains.length > 0) {
    memoizedChainsByIdMap = chains.reduce(
      (acc, chain) => {
        acc[chain.chainId] = chain;
        return acc;
      },
      {} as Record<string, ChainData>
    );
    return memoizedChainsByIdMap;
  }
  return null;
};

/**
 * Returns an array of the available chains based on the provided config and direction
 */
export const useSquidChains = (direction?: SwapDirection) => {
  const { chains: allSquidChains = [] } = useSquid();
  const config = useConfigStore((state) => state.config);

  const chains = useMemo(() => {
    return filterChains({
      chains: allSquidChains,
      config: {
        disabledChains: config.disabledChains,
        availableChains: config.availableChains,
      },
      direction,
    });
  }, [
    allSquidChains,
    config.disabledChains,
    config.availableChains,
    direction,
  ]);

  const chainsByIdMap = useMemo(
    () => createChainsByIdMap(chains),
    // Only re-render if the chains id array has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chains.map((c) => c.chainId)]
  );

  const findChain = useCallback(
    (chainId: string | undefined): ChainData | undefined => {
      return chainId ? chainsByIdMap?.[chainId] : undefined;
    },
    [chainsByIdMap]
  );

  const getChainType = useCallback(
    (chainId?: string): ChainType | undefined => {
      if (!chainId) return undefined;
      const chain = findChain(chainId);
      return chain?.chainType;
    },
    [findChain]
  );

  const {
    evmChains,
    cosmosChains,
    suiChains,
    stellarChains,
    xrplChains,
    cantonChains,
  } = useMemo(() => {
    return chains.reduce(
      (acc, chain) => {
        switch (chain.chainType) {
          case ChainType.EVM:
            acc.evmChains.push(chain as EvmChain);
            break;
          case ChainType.COSMOS:
            acc.cosmosChains.push(chain as CosmosChain);
            break;
          case ChainType.SUI:
            acc.suiChains.push(chain);
            break;
          case ChainType.XRPL:
            acc.xrplChains.push(chain);
            break;
          case ChainType.STELLAR:
            acc.stellarChains.push(chain);
            break;
          case ChainType.CANTON:
            acc.cantonChains.push(chain);
            break;
        }

        return acc;
      },
      {
        evmChains: [] as EvmChain[],
        cosmosChains: [] as CosmosChain[],
        suiChains: [] as ChainData[],
        xrplChains: [] as ChainData[],
        stellarChains: [] as ChainData[],
        cantonChains: [] as ChainData[],
      }
    );
  }, [chains]);

  const { supportedSourceChains, supportedDestinationChains } = useMemo(() => {
    const chainIds = chains.map((c) => c.chainId);

    const srcChainIds = getSupportedChainIdsForDirection(
      chainIds,
      config,
      "from"
    );
    const destChainIds = getSupportedChainIdsForDirection(
      chainIds,
      config,
      "to"
    );

    return {
      supportedSourceChains: chains.filter((c) =>
        srcChainIds.includes(c.chainId)
      ),
      supportedDestinationChains: chains.filter((c) =>
        destChainIds.includes(c.chainId)
      ),
    };
  }, [chains, config]);

  return {
    supportedSourceChains,
    supportedDestinationChains,
    chains,
    evmChains,
    cosmosChains,
    suiChains,
    xrplChains,
    stellarChains,
    cantonChains,
    getChainType,
    findChain,
  };
};
